/**
 * The seam where the follow rules meet the world: the lifecycle that takes a finished dog's
 * body kinematic and hands it back, and the one frame loop that walks it (design D5, D6, D9
 * of `robot-dog-follows-player`).
 *
 * Everything that decides anything lives in `src/game/` — `trail.ts` keeps the queue,
 * `follow.ts` answers where the dog ends up this frame, `dogGait.ts` answers what its legs
 * are doing. This file only converts: it reads the camera for where the player stands,
 * reads the trail, and writes the answers onto a Rapier body and a handful of `Group`s. It
 * holds no React state, writes nothing to the store, and allocates nothing per frame — the
 * scratch objects below are module-level because there is exactly one following dog (D2),
 * the same reasoning `PartBeacon` and `dogGait`'s shared pose already use.
 *
 * The lifecycle and the pose live together on purpose. Entering the follow state has to
 * right the body *and* start the follower pose from where that body actually ended up; if
 * those two happened in different files they could disagree by a frame, and a frame of
 * disagreement is a dog that lurches on the first step. That is also why the stand-up
 * happens in the frame loop rather than in the effect: a thrown dog is handed back the
 * instant it leaves the hands, still travelling at throw speed, and it has to be allowed to
 * land before it gets up. The effect therefore only records the *intent* to walk; the loop
 * decides when that intent becomes a kinematic body.
 */

import { useFrame, useThree } from '@react-three/fiber';
import { useRapier, type RapierRigidBody } from '@react-three/rapier';
import { useEffect, useMemo, useRef, type RefObject } from 'react';
import { Euler, Quaternion, Vector3 } from 'three';
import type { Group } from 'three';
import { createFollowerPose, stepFollow, type FollowerPose } from '../game/follow';
import { gaitPose, LEG_IDS, STRIDE_LENGTH } from '../game/dogGait';
import { isPlayable } from '../game/phase';
import { clearTrail, pruneTrail } from '../game/trail';
import type { Vec3 } from '../game/profile/types';
import { useGameStore } from '../store/gameStore';
import { EYE_HEIGHT } from './constants';
import { CARRYABLE_GROUPS, COMPANION_GROUPS } from './objectRegistry';
import { getPlayerTrail } from './playerTrail';

/**
 * The same clamp `Player.tsx` puts on its own loop: one arbitrarily long frame — a
 * backgrounded tab, a GC pause — would otherwise move the dog metres in a single step and
 * integrate a whole stride into one frame of gait.
 */
const MAX_FRAME_DELTA = 0.1;

/**
 * Wags per second while standing still. The one clock-driven number in the gait, and D6
 * says why it has to be one: a wag is not a footfall, so a stopped dog still wags.
 */
const WAG_CYCLES_PER_SECOND = 1.2;

/**
 * Radians of nod per metre of `gaitPose`'s head bob. The pure gait reports the bob as a
 * displacement, but the head group's own `position` carries its neck pivot (`dogGeometry`),
 * and only its rotation is free for an animator to write — so the bob is spent as a nod
 * about that pivot instead, which reads the same and cannot clobber the pivot.
 */
const HEAD_NOD_PER_METRE = 8;

/** Scratch. One dog (D2), so module-level and reused every frame rather than allocated. */
const playerFeet: Vec3 = { x: 0, y: 0, z: 0 };
const nextTranslation = new Vector3();
const nextRotation = new Quaternion();
const nextEuler = new Euler();
const forward = new Vector3();
const UP = new Vector3(0, 1, 0);
const ZERO = { x: 0, y: 0, z: 0 };

/** The two groups one leg is animated through — the hip pivot and the knee inside it. */
export type LegJointRefs = {
  hip: RefObject<Group | null>;
  knee: RefObject<Group | null>;
};

/**
 * Everything the gait writes to. `RobotDogBody` hands these straight to `dogGeometry`'s
 * components; nothing else reads them.
 */
export type DogRig = {
  /** The whole dog inside the body, so bob and roll never move the collider. */
  rig: RefObject<Group | null>;
  head: RefObject<Group | null>;
  tail: RefObject<Group | null>;
  legs: Record<string, LegJointRefs | undefined>;
};

/**
 * Slow enough to count as come to rest, in metres per second. Only ever compared against a
 * body that has just been dropped or thrown, so it has to be under the speed a dog settles
 * and tumbles at and over nothing in particular — a dog still sliding a few centimetres a
 * second may as well stand up where it is.
 */
const REST_SPEED = 0.3;

/** Whether a body has finished flying, so the dog on it can get to its feet. */
function isAtRest(body: RapierRigidBody): boolean {
  const velocity = body.linvel();
  return Math.hypot(velocity.x, velocity.y, velocity.z) <= REST_SPEED;
}

/** The yaw a body is already turned to, in `follow.ts`'s convention (0 faces -Z). */
function yawOf(body: RapierRigidBody): number {
  const rotation = body.rotation();
  nextRotation.set(rotation.x, rotation.y, rotation.z, rotation.w);
  forward.set(0, 0, -1).applyQuaternion(nextRotation);
  return Math.atan2(-forward.x, -forward.z);
}

/**
 * Walks the dog whose body is `bodyRef` while `isFollowing`, and returns the groups its
 * gait is written onto. `boxHeight` is the host's own box height: the trail records the
 * player's *feet*, the body's translation is its *centre*, and half a box is the whole
 * difference between the two (design D1 — the dog's height comes from the trail and from
 * nothing else, so this is the only conversion there is).
 */
export function useDogFollow(
  bodyRef: RefObject<RapierRigidBody | null>,
  isFollowing: boolean,
  boxHeight: number,
): DogRig {
  const camera = useThree((state) => state.camera);
  const { rapier } = useRapier();

  const rig = useMemo<DogRig>(() => {
    const legs: Record<string, LegJointRefs> = {};
    for (const id of LEG_IDS) legs[id] = { hip: { current: null }, knee: { current: null } };
    return { rig: { current: null }, head: { current: null }, tail: { current: null }, legs };
  }, []);

  /** Non-null exactly while the dog is walking: the frame loop's own on/off switch. */
  const poseRef = useRef<FollowerPose | null>(null);
  /** Whether the dog should be walking — true from the attach that finished it, or from
   *  the moment it is set down again, and false the instant it is picked up or restarted. */
  const wantsToWalkRef = useRef(false);
  /** Whether this hook is the one currently holding the body kinematic. */
  const holdsBodyRef = useRef(false);
  const stridePhaseRef = useRef(0);
  const wagPhaseRef = useRef(0);

  useEffect(() => {
    if (!isFollowing) return;
    const body = bodyRef.current;
    if (!body) return;

    // Wanting to walk and walking are not the same frame. A thrown dog is still flying
    // when `release()` hands it back (`CarryController`), and a dog that stood up in
    // mid-air would never land, so the stand-up waits for the body to come to rest — see
    // the frame loop below.
    wantsToWalkRef.current = true;

    return () => {
      wantsToWalkRef.current = false;
      poseRef.current = null;
      // A dog still in the air was never taken, and must not be: zeroing the velocity of
      // a body mid-throw would stop it dead in the air.
      if (!holdsBodyRef.current) return;
      holdsBodyRef.current = false;
      // Handing it back the way a drop does — dynamic first, then the groups, then the
      // velocities, because velocities set on a kinematic body are discarded
      // (`releaseBody`, `parkBody`). This runs on a pick-up as well as on a restart, and
      // it is what lets `CarryController`'s grab — which recognises an ungrabbed body by
      // its not being kinematic yet — see the dog as something it still has to take.
      body.setBodyType(rapier.RigidBodyType.Dynamic, true);
      body.collider(0).setCollisionGroups(CARRYABLE_GROUPS);
      body.setLinvel(ZERO, true);
      body.setAngvel(ZERO, true);
    };
  }, [isFollowing, bodyRef, rapier]);

  useFrame((_, rawDelta) => {
    const body = bodyRef.current;
    if (!wantsToWalkRef.current || !body) return;
    // Read the phase imperatively: `useFrame` keeps running while the world is paused, and
    // subscribing would re-render the dog on every pause (D9). A paused dog holds its
    // pose, its stride phase and its wag, and resumes from exactly there.
    if (!isPlayable(useGameStore.getState().phase)) return;

    let pose = poseRef.current;
    if (!pose) {
      // Still settling — a thrown dog flies and lands as a thrown object does, and only
      // then gets up (design D5's "resumes following from where it came to rest"). A dog
      // set down, or one finished standing on its table, is already at rest and gets up on
      // this very frame.
      if (!isAtRest(body)) return;

      // The stand-up: kinematic, tilt discarded so only the yaw it happened to be lying at
      // survives — this is also what rights a dog assembled on its side — passable (D4),
      // and the trail forgotten, so it walks from where it is rather than chasing wherever
      // the player went while it was in their hands.
      const yaw = yawOf(body);
      body.setBodyType(rapier.RigidBodyType.KinematicPositionBased, true);
      body.setRotation(nextRotation.setFromAxisAngle(UP, yaw), true);
      body.collider(0).setCollisionGroups(COMPANION_GROUPS);
      clearTrail(getPlayerTrail());
      holdsBodyRef.current = true;

      const centre = body.translation();
      pose = createFollowerPose(
        { x: centre.x, y: centre.y - boxHeight / 2, z: centre.z },
        yaw,
      );
      poseRef.current = pose;
      // Nothing to walk yet: the body was just teleported upright, and the first step
      // belongs to the next frame, with a trail that starts here.
      return;
    }

    const delta = Math.min(rawDelta, MAX_FRAME_DELTA);
    const trail = getPlayerTrail();

    // Where the player stands, in the trail's own convention: feet, not eye.
    playerFeet.x = camera.position.x;
    playerFeet.y = camera.position.y - EYE_HEIGHT;
    playerFeet.z = camera.position.z;

    pruneTrail(trail, pose.position, playerFeet);
    stepFollow(pose, trail, playerFeet, delta);

    body.setNextKinematicTranslation(
      nextTranslation.set(pose.position.x, pose.position.y + boxHeight / 2, pose.position.z),
    );
    // YXZ: the yaw turns the dog in the world and the climb pitch tips it in its own
    // frame, which is the order that keeps a nose-up dog pointing along its path rather
    // than leaning sideways out of it.
    body.setNextKinematicRotation(
      nextRotation.setFromEuler(nextEuler.set(pose.pitch, pose.yaw, 0, 'YXZ')),
    );

    // Distance, not `delta` (D6): this is the whole reason the paws do not skate. Wrapped
    // to one cycle so neither phase drifts into the range where a float stops resolving
    // it; every term of the gait has a period that divides 1, so wrapping is invisible.
    const strideAdvance = pose.distanceMoved / STRIDE_LENGTH;
    stridePhaseRef.current = (stridePhaseRef.current + strideAdvance) % 1;
    wagPhaseRef.current =
      (wagPhaseRef.current +
        (pose.distanceMoved > 0 ? strideAdvance : delta * WAG_CYCLES_PER_SECOND)) %
      1;

    // Called exactly once per frame: `gaitPose` hands back a module-owned object it
    // mutates in place, so a second call would quietly rewrite the first's answers.
    const gait = gaitPose(stridePhaseRef.current, wagPhaseRef.current);

    const rigGroup = rig.rig.current;
    if (rigGroup) {
      rigGroup.position.y = gait.bodyBob;
      rigGroup.rotation.z = gait.bodyRoll;
    }
    if (rig.head.current) rig.head.current.rotation.x = gait.headBob * HEAD_NOD_PER_METRE;
    if (rig.tail.current) rig.tail.current.rotation.y = gait.tailAngle;

    for (const id of LEG_IDS) {
      const joint = rig.legs[id];
      if (!joint) continue;
      const leg = gait.legs[id];
      if (joint.hip.current) joint.hip.current.rotation.x = leg.hip;
      if (joint.knee.current) joint.knee.current.rotation.x = leg.knee;
    }
  });

  return rig;
}
